import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axios/axios-instance";
import { pathAPI } from "../../utils/pathApi";
import {
  LoyaltyPointSummary,
  LoyaltyPointTransaction,
  defaultLoyaltySummary,
  unwrap,
  unwrapPageItems,
  unwrapPageTotal,
} from "./userLoyalty";

/** Loyalty summary + transactions + points adjustment of one app user. */
export const useUserLoyalty = (id?: string) => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<LoyaltyPointSummary>(defaultLoyaltySummary);
  const [transactions, setTransactions] = useState<LoyaltyPointTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [adjustmentPoints, setAdjustmentPoints] = useState("");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [adjustmentError, setAdjustmentError] = useState("");
  const [adjustmentSaving, setAdjustmentSaving] = useState(false);

  const fetchLoyalty = useCallback(
    async (nextPage: number, nextSize: number) => {
      if (!id) return;

      setLoading(true);
      setError("");
      try {
        const [summaryResponse, transactionResponse] = await Promise.all([
          axiosInstance.get(`${pathAPI.appUsers}/${id}/loyalty/summary`),
          axiosInstance.get(`${pathAPI.appUsers}/${id}/loyalty/transactions`, {
            params: { page: nextPage, size: nextSize },
          }),
        ]);

        const transactionPayload = unwrap(transactionResponse);
        const items = unwrapPageItems(transactionPayload);

        setSummary({ ...defaultLoyaltySummary, ...unwrap(summaryResponse) });
        setTransactions(items);
        setTotal(unwrapPageTotal(transactionPayload, items.length));
      } catch (err) {
        console.error("Error fetching loyalty points:", err);
        setError(t("user_app.loyalty.error_load"));
      } finally {
        setLoading(false);
      }
    },
    [id, t]
  );

  useEffect(() => {
    fetchLoyalty(0, rowsPerPage);
  }, [id]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchLoyalty(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const nextSize = parseInt(event.target.value, 10);
    setRowsPerPage(nextSize);
    setPage(0);
    fetchLoyalty(0, nextSize);
  };

  const handleAdjustPoints = async () => {
    if (!id) return;

    const points = Number(adjustmentPoints);
    const reason = adjustmentReason.trim();
    if (!Number.isInteger(points) || points === 0) {
      setAdjustmentError(t("user_app.loyalty.error_points"));
      return;
    }
    if (!reason) {
      setAdjustmentError(t("user_app.loyalty.error_reason"));
      return;
    }

    setAdjustmentSaving(true);
    setAdjustmentError("");
    try {
      await axiosInstance.post(`${pathAPI.appUsers}/${id}/loyalty/adjustments`, { points, reason });
      setAdjustmentPoints("");
      setAdjustmentReason("");
      await fetchLoyalty(0, rowsPerPage);
      setPage(0);
    } catch (err) {
      console.error("Error adjusting loyalty points:", err);
      setAdjustmentError(t("user_app.loyalty.error_adjust"));
    } finally {
      setAdjustmentSaving(false);
    }
  };

  return {
    summary,
    transactions,
    loading,
    error,
    page,
    rowsPerPage,
    total,
    adjustmentPoints,
    setAdjustmentPoints,
    adjustmentReason,
    setAdjustmentReason,
    adjustmentError,
    adjustmentSaving,
    handleChangePage,
    handleChangeRowsPerPage,
    handleAdjustPoints,
  };
};

export type UserLoyalty = ReturnType<typeof useUserLoyalty>;
